
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { CustomEventListenerOrEventListenerObject, Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, MARGIN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { UID } from "@cervval.js/common-action/src/types";
import { CheckboxOptions, CjsCheckbox } from "../checkbox/checkbox";

/** Orientation of checkbox group */
export enum CheckboxGroupOrientation {
    HORIZONTAL = "horizontal",
    VERTICAL = "vertical"
}

export type CheckboxGroupCheckboxConfig = (Partial<CheckboxOptions> & Required<{ uid: string }>);

export interface CheckboxGroupOptions {

    /** List of checkbox option with uid. Uid is compulsory because we must differentiate all radios checkboxes */
    checkboxOptions: CheckboxGroupCheckboxConfig[];

    /** Set to true to disable all Checkboxes of group */
    disabled?: boolean;

    /**
     * Orientation of checkbox group
     * @default vertical
     */
    orientation: CheckboxGroupOrientation;

    /** 
     * Allow multiple values selection
     * ! Use getSelectedValues and setSelectedValues if this mode is enable to retrieve/set all selection
     */
    allowMultipleValues?: boolean;
}

export const defaultCheckboxOptions: CheckboxGroupOptions = {

    /** List of checkbox option with uid. Uid is compulsory because we must differentiate all radios checkboxes */
    checkboxOptions: [],

    /**
     * Orientation of checkbox group
     * @default vertical
     */
    orientation: CheckboxGroupOrientation.VERTICAL
};

/** Attribute name of the group */
const GROUP_ATTRIBUTE_NAME = "checkbox-group";

/**
 * Group of radio button 
 */
@CustomElement({
    name: "cjs-checkbox-group"
})
export class CjsCheckboxGroup extends WebComponent {

    @TypedWebComponentOptions(defaultCheckboxOptions)
    public readonly options!: WebComponentOptions<CheckboxGroupOptions>;

    /** 
     * Triggered when value of CheckboxGroup has change 
     */
    @Event(NoBubblesEventInit)
    public changeEvent!: EventHandler<UID[]>;

    /** list of checkbox component in the group */
    public checkboxes: CjsCheckbox[] = [];

    /** Store change listeners by checkboxes */
    public changeListenersByCheckbox: Record<UID, CustomEventListenerOrEventListenerObject<void>> = {};

    constructor(options?: Partial<CheckboxGroupOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** 
     * Component refresh 
     */
    public override refresh(reason?: string, p?: { values?: UID[] }): void {

        // Unsubscribe previous checkEvent listeners
        for (const [uid, listener] of Object.entries(this.changeListenersByCheckbox)) {
            const ck = this.checkboxes.find(checkbox => checkbox.options.uid === uid);
            if (ck) {
                ck.checkEvent.off(listener);
            }
        }

        // Reset
        empty(this);
        this.checkboxes = [];

        // Create new checkboxes
        this.options.checkboxOptions.forEach(checkboxOption => {
            const checkbox = new CjsCheckbox(checkboxOption);
            checkbox.components.formCheckInput.setAttribute("name", GROUP_ATTRIBUTE_NAME + "_" + this.componentNumber);
            this.checkboxes.push(checkbox);
        });
        this.append(...this.checkboxes);

        // Refresh checkboxes values if given in refresh (ex: from Editor between two refresh)
        if (p?.values) {
            this._refreshCheckboxesValues(p.values, { dispatchEvent: false });
        }

        // Refresh disabled state
        this.refreshDisabled();

        // Register a checkEvent listener on all checkboxes, to set value of group
        for (const ck of this.checkboxes) {
            const uid = ck.options.uid ?? "";
            // Callback for checkboxes toggle components
            const listener: EventListenerObject = {
                handleEvent: () => {
                    if (this.options.allowMultipleValues) {
                        this._refreshCheckboxesValues(this.checkboxes.filter(c => c.getBooleanValue()).map(c => c.options.uid ?? ""));
                    } else {
                        this._refreshCheckboxesValues([uid]);
                    }
                }
            };
            this.changeListenersByCheckbox[uid] = listener;

            // if no multiple values, ensure only one checkbox is checked
            if (!this.options.allowMultipleValues) {
                ck.checkEvent.on(listener);
            } else {
                ck.toggleEvent.on(listener);
            }
        }

        // Refresh checkboxes orientation 
        this._refreshOrientation();
    }

    /** Refresh all checkboxes values and dispatch valueChange event */
    private _refreshCheckboxesValues(uids: UID[], p?: { dispatchEvent?: boolean }): void {
        for (const ck of this.checkboxes) {
            const isCkChecked = ck.options.uid != null && uids.includes(ck.options.uid);
            ck.setValue(isCkChecked, false);
        }

        if (p?.dispatchEvent !== false) {
            this.changeEvent.emit(uids);
        }
    }

    /** Refresh checkboxes orientation */
    private _refreshOrientation(): void {
        toggleClass(this, this.options.orientation === CheckboxGroupOrientation.HORIZONTAL, FLEX_CONTAINER);
        toggleClass(this, this.options.orientation === CheckboxGroupOrientation.VERTICAL, FLEX_CONTAINER_COLUMN);

        this.checkboxes.forEach(ck => {
            toggleClass(ck, this.options.orientation === CheckboxGroupOrientation.HORIZONTAL, MARGIN.MS_2);
        });
    }

    /**
     * Select a checkbox with his uid
     */
    public setSelectedValue(uid: UID, options?: { doNotDispatchEvent?: boolean }): void {
        for (const checkbox of this.checkboxes) {
            checkbox.setValue(uid === this.getCheckboxUid(checkbox), !options?.doNotDispatchEvent);
        }
    }

    /**
     * Select the checkboxes with their uids
     */
    public setSelectedValues(uids: UID[], options?: { doNotDispatchEvent?: boolean }): void {
        for (const checkbox of this.checkboxes) {
            checkbox.setValue(uids.includes(checkbox.options.uid ?? ""), !options?.doNotDispatchEvent);
        }
    }

    /**
     * Return the UID of the first checkbox selected
     */
    public getSelectedValue(): UID | undefined {

        for (const checkbox of this.checkboxes) {
            if (checkbox.getBooleanValue()) {
                return this.getCheckboxUid(checkbox);
            }
        }

        return undefined;
    }

    /**
     * Return the UID of the checkbox selected
     */
    public getSelectedValues(): UID[] {
        const selectedValues: UID[] = [];
        for (const checkbox of this.checkboxes) {
            if (checkbox.getBooleanValue()) {
                selectedValues.push(this.getCheckboxUid(checkbox));
            }
        }
        return selectedValues;
    }

    /**
     * Get a checkbox uid from his options.
     * If not defined, use componentNumber
     */
    public getCheckboxUid(checkbox: CjsCheckbox): UID {
        return checkbox.options.uid ?? "checkbox" + checkbox.componentNumber;
    }


    /** Refresh disabled state */
    public refreshDisabled(): void {
        for (const ck of this.checkboxes) {
            const ckConfig = this.options.checkboxOptions.find(config => config.uid === ck.options.uid);
            ck.options.setAndRefreshIfChanged({ disabled: ckConfig?.disabled ?? this.options.disabled });
        }
    }
}